import React, { useState } from 'react';
import { createSecretaryUserApi, type SecretaryUser } from '../../../shared/api/secretaryApi';
import type { SupportProvider } from '../../../shared/types/common';

interface RegistrationModalProps {
  email: string;
  name: string;
  accessToken: string;
  onComplete: (user: SecretaryUser, token: string, expiresAt: number) => void;
  onCancel: () => void;
  onCredentialExpired: () => void;
}

const RegistrationModal: React.FC<RegistrationModalProps> = ({ 
  email, 
  name, 
  accessToken, 
  onComplete, 
  onCancel,
  onCredentialExpired
}) => {
  const [formData, setFormData] = useState({
    name: name,
    clinicName: '',
    provider: '' as SupportProvider | '',
    patientLanguage: '' as 'hebrew' | 'arabic' | '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const providers: SupportProvider[] = ['כללית', 'מאוחדת'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.clinicName || !formData.provider || !formData.patientLanguage) {
      setError('נא למלא את כל השדות');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await createSecretaryUserApi({
        email,
        name: formData.name,
        clinicName: formData.clinicName,
        provider: formData.provider,
        patientLanguage: formData.patientLanguage,
        idToken: accessToken,
      });

      onComplete(result.secretary, result.token, result.expiresAt);
    } catch (err) {
      if ((err as { status?: number }).status === 401) { onCredentialExpired(); return; }
      setError('שמירת הפרטים נכשלה. אנא נסה שוב.');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">ברוך הבא!</h2>
        <p className="text-slate-600 mb-6">נא למלא את הפרטים הבאים</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              שם מלא *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="הכנס שם מלא"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              שם המרפאה *
            </label>
            <input
              type="text"
              value={formData.clinicName}
              onChange={(e) => setFormData({ ...formData, clinicName: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="הכנס שם המרפאה"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              קופת חולים *
            </label>
            <div className="grid grid-cols-2 gap-3">
              {providers.map((provider) => (
                <button
                  key={provider}
                  type="button"
                  onClick={() => setFormData({ ...formData, provider })}
                  disabled={loading}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.provider === provider
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-slate-300 hover:border-indigo-400'
                  } disabled:opacity-50`}
                >
                  <div className="text-center font-medium text-slate-800">{provider}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              שפת המטופלים *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, patientLanguage: 'hebrew' })}
                disabled={loading}
                className={`p-4 border-2 rounded-lg transition-all ${
                  formData.patientLanguage === 'hebrew'
                    ? 'border-indigo-600 bg-indigo-50'
                    : 'border-slate-300 hover:border-indigo-400'
                } disabled:opacity-50`}
              >
                <div className="text-center font-medium text-slate-800">עברית</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, patientLanguage: 'arabic' })}
                disabled={loading}
                className={`p-4 border-2 rounded-lg transition-all ${
                  formData.patientLanguage === 'arabic'
                    ? 'border-indigo-600 bg-indigo-50'
                    : 'border-slate-300 hover:border-indigo-400'
                } disabled:opacity-50`}
              >
                <div className="text-center font-medium text-slate-800">عربي</div>
              </button>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 transition-colors disabled:opacity-50"
            >
              ביטול
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'שמור והמשך'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationModal;
